import React, { useEffect, useLayoutEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import StackButton from "@/components/buttons/StackButton";
import { FormPageNameField } from "@/components/forms/FormPageNameField";
import StatusToggle from "@/components/toggles/StatusToggle";
import { FormToggleListPageSkeleton } from "@/components/skeletons";
import { getGroupById, updateGroup } from "@/services/group.service";
import { fetchAllRoles } from "@/services/role.service";
import { BUTTONS, HEADINGS, LABELS } from "@/utils/constants/appConstants/commonConstants";
import { ALL_ROLES } from "@/utils/constants/constants";
import { storeInLocalStorage } from "@/utils/functions/helperFunctions";
import { errorToasterMessage } from "@/utils/functions/toaster";

const fetchGroupById = (id: string) =>
    new Promise<any>((resolve, reject) => {
        getGroupById(id, resolve, reject);
    });

const fetchRolesList = () =>
    new Promise<any>((resolve, reject) => {
        fetchAllRoles(resolve, reject);
    });

const EditGroup = () => {
    const params = useParams();
    const navigate = useNavigate();
    const group_id = params.id || "";
    const [groupDetails, setGroupDetails] = useState<any>({});
    const [allRoles, setAllRoles] = useState<any>([]);
    const [checkedCount, setCheckedCount] = useState(0);
    const [loader, setLoader] = useState(false);
    const [pageReady, setPageReady] = useState(false);

    useLayoutEffect(() => {
        setPageReady(false);
        setGroupDetails({});
        setAllRoles([]);
        setCheckedCount(0);
    }, [group_id]);

    useEffect(() => {
        let cancelled = false;

        const loadPage = async () => {
            try {
                const [groupResponse, rolesResponse] = await Promise.all([
                    fetchGroupById(group_id),
                    fetchRolesList(),
                ]);

                if (cancelled) {
                    return;
                }

                const group = groupResponse.data;
                storeInLocalStorage(ALL_ROLES, rolesResponse.data.roles);

                let checkedCountLocal = 0;
                const newRolesList = rolesResponse.data.roles.map((role: any) => {
                    const checked = group?.roles?.some((obj: { name: string }) => obj.name === role.name) ?? false;
                    checkedCountLocal += checked ? 1 : 0;
                    return { ...role, checked };
                });

                setGroupDetails(group);
                setCheckedCount(checkedCountLocal);
                setAllRoles(newRolesList);
                setPageReady(true);
            } catch (error) {
                if (!cancelled) {
                    console.log(error);
                    setPageReady(true);
                }
            }
        };

        loadPage();

        return () => {
            cancelled = true;
        };
    }, [group_id]);

    const onChangeRole = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newRoleList = allRoles.map((role: any) => {
            if (e.target.name === role.name) {
                const newRole = { ...role, checked: e.target.checked };
                setCheckedCount(checkedCount + (e.target.checked ? 1 : -1));
                return newRole;
            }
            return role;
        });
        setAllRoles(newRoleList);
    };

    const handleEditGroup = () => {
        setLoader(true);
        updateGroup(
            group_id,
            JSON.stringify({ roles: allRoles.filter((role: any) => role.checked).map((role: any) => role.id) }),
            () => {
                setLoader(false);
                navigate(-1);
            },
            (error: any) => {
                setLoader(false);
                errorToasterMessage(error?.data.message);
                navigate(-1);
                console.log(error);
            }
        );
    };

    if (!pageReady) {
        return <FormToggleListPageSkeleton sectionTitle={HEADINGS.ROLES} />;
    }

    const isSaveDisabled = checkedCount === 0 || !groupDetails.name?.trim();

    return (
        <div className="gs-form-page">
            <div className="gs-form-page-header">
                <h3 className="gs-form-page-title">
                    <strong id="edit-group-name-title">{groupDetails.name}</strong>
                </h3>
                <StackButton
                    secondaryVariant="light"
                    primaryId="edit-group-save-btn"
                    secondaryId="edit-group-cancel-btn"
                    primaryButton={BUTTONS.SAVE}
                    secondaryButton={BUTTONS.CANCEL}
                    handlePrimary={handleEditGroup}
                    handleSecondary={() => navigate(-1)}
                    disabled={isSaveDisabled}
                    isLoading={loader}
                />
            </div>

            <form
                onSubmit={(event) => {
                    event.preventDefault();
                    if (!isSaveDisabled && !loader) {
                        handleEditGroup();
                    }
                }}
            >
                <FormPageNameField
                    label={LABELS.GROUP_NAME}
                    labelId="edit-group-name-label"
                    inputId="edit-group-name-input"
                    value={groupDetails.name || ""}
                    disabled
                    readOnly
                />

                <div className="gs-form-page-section">
                    <h5 className="gs-form-section-title gs-form-section-title--spaced" id="edit-group-role-title">
                        {HEADINGS.ROLES}
                    </h5>
                    <div className="gs-list-page-card">
                        <ul className="gs-detail-list gs-form-toggle-list">
                            {allRoles.map((role: any, key: number) => (
                                <li className="gs-detail-list-item" key={role.id}>
                                    <StatusToggle
                                        index={key}
                                        onChange={onChangeRole}
                                        name={role.name as string}
                                        id={role.id as string}
                                        checked={role.checked}
                                        disabled={role.name === "superadmin"}
                                    >
                                        <p className="m-0" id={`edit-group-role-name-label-${key}`}>{role.name}</p>
                                    </StatusToggle>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default EditGroup;
